import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, LogOut, Clock, AlertTriangle, ShieldCheck, Building2, UserCog, KeyRound, Loader2, Search, Users, ChevronRight, BusFront, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { displayName } from "@/lib/format";
import { officerHasConsole, officerAllowedModules } from "@/lib/access";
import { toast } from "sonner";
import LicenseScanner from "@/components/LicenseScanner";
import StaffClockTile from "@/components/StaffClockTile";
import useWakeLock from "@/hooks/useWakeLock";
import { FitToScreen } from "@/components/FitToScreen";
import { ParentPinSignin } from "@/components/daycare/ParentPinSignin";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const ACTIONS = [
  { key: "drop_off", label: "Drop Off", subtitle: "Arrival", Icon: LogIn, accent: "primary" },
  { key: "pickup", label: "Pickup", subtitle: "End of day", Icon: LogOut, accent: "dark" },
  { key: "tardy", label: "Tardy", subtitle: "Arriving late", Icon: Clock, accent: "amber" },
  { key: "early_dismissal", label: "Early Dismissal", subtitle: "Leaving early", Icon: AlertTriangle, accent: "orange" },
];

// "HH:MM" -> minutes since midnight, or null if blank/invalid.
const _hhmmToMins = (v) => {
  if (!v || typeof v !== "string") return null;
  const m = v.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

// Returns the subset of action keys to show right now given the school's schedule.
// Rules:
//   - In drop-off window  -> only drop_off
//   - In pickup window    -> only pickup
//   - Otherwise           -> tardy + early_dismissal
//   - If a window's start/end is blank, that window is treated as "not configured"
//     and we fall through to the tardy/early_dismissal set.
//   - If no windows are configured at all -> show all 4 actions (back-compat).
function visibleActionKeys(building, nowDate) {
  const dStart = _hhmmToMins(building.drop_off_start);
  const dEnd = _hhmmToMins(building.drop_off_end);
  const pStart = _hhmmToMins(building.pickup_start);
  const pEnd = _hhmmToMins(building.pickup_end);
  const anyConfigured = dStart !== null || dEnd !== null || pStart !== null || pEnd !== null;
  if (!anyConfigured) return ACTIONS.map((a) => a.key);
  const cur = nowDate.getHours() * 60 + nowDate.getMinutes();
  if (dStart !== null && dEnd !== null && cur >= dStart && cur < dEnd) return ["drop_off"];
  if (pStart !== null && pEnd !== null && cur >= pStart && cur < pEnd) return ["pickup"];
  return ["tardy", "early_dismissal"];
}

export default function SchoolKioskHome({ daycare = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  // Keep the tablet awake while the school kiosk is open.
  useWakeLock(true);
  const [building, setBuilding] = useState({
    name: "", address: "", logo_data_url: "",
    drop_off_start: "", drop_off_end: "",
    pickup_start: "", pickup_end: "",
  });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  // Re-evaluate visible actions every 30s so the kiosk transitions automatically through the day.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Flow state
  const [actionType, setActionType] = useState(null); // 'drop_off' | 'pickup' | 'tardy' | 'early_dismissal'
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanData, setScanData] = useState(null); // { full_name, license_number, photo }
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pickupError, setPickupError] = useState(""); // persistent rejection banner inside the dialog
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const b = await api.get("/settings/building");
        setBuilding({
          name: b.data.name || "",
          address: b.data.address || "",
          logo_data_url: b.data.effective_logo_data_url || b.data.logo_data_url || "",
          drop_off_start: b.data.drop_off_start || "",
          drop_off_end: b.data.drop_off_end || "",
          pickup_start: b.data.pickup_start || "",
          pickup_end: b.data.pickup_end || "",
        });
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allowedKeys = visibleActionKeys(building, now);
  const shownActions = ACTIONS.filter((a) => allowedKeys.includes(a.key));

  const startAction = (key) => {
    setActionType(key);
    setScanData(null);
    setTypedName("");
    setPickupError("");
    if (key === "drop_off" || key === "tardy") {
      // No license required — ask parent to type the student's name
      setNameDialogOpen(true);
    } else {
      // Pickup / early_dismissal — scan license first, then ask for the student's name
      setScannerOpen(true);
    }
  };

  const onScanned = (data) => {
    setScanData(data);
    setScannerOpen(false);
    setNameDialogOpen(true);
  };

  const submitTypedName = async (e) => {
    e?.preventDefault();
    if (!typedName.trim() || submitting) return;
    setSubmitting(true);
    setPickupError("");
    try {
      const body = {
        student_name: typedName.trim(),
        event_type: actionType,
        picker_name: scanData?.full_name || "",
        picker_license: scanData?.license_number || "",
        picker_photo: scanData?.photo || "",
      };
      const res = await api.post("/student-events/by-name", body);
      toast.success(`${res.data.student_name} · ${actionType.replace("_", " ")} recorded`);
      cancelFlow();
    } catch (err) {
      // Surface the rejection prominently inside the dialog so it can never be mistaken
      // for a successful pickup/check-out. The dialog stays open for a retry.
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not record event";
      setPickupError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelFlow = () => {
    setActionType(null);
    setScannerOpen(false);
    setNameDialogOpen(false);
    setScanData(null);
    setTypedName("");
    setPickupError("");
  };

  const confirmEndShift = async (e) => {
    e?.preventDefault();
    if (!pwd || verifying) return;
    setVerifying(true);
    try {
      await api.post("/auth/verify-password", { password: pwd });
      await logout();
      toast.success("Shift ended");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setVerifying(false);
    }
  };

  const actionLabel = ACTIONS.find((a) => a.key === actionType)?.label || "";

  return (
    <div
      className="relative h-screen w-screen overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 15% 10%, rgba(37,99,235,0.18), transparent 60%)," +
          "radial-gradient(900px 500px at 95% 90%, rgba(14,165,233,0.18), transparent 55%)," +
          "linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #e0f2fe 100%)",
      }}
      data-testid="school-kiosk"
    >
      <div className="relative z-10 flex h-full flex-col safe-area">
        <header className="flex items-center justify-between px-12 py-8 [@media(max-height:820px)]:py-4">
          <div className="flex items-center gap-3 rounded-full bg-white/80 px-5 py-2.5 backdrop-blur-xl border border-white/60 shadow-sm">
            {building.logo_data_url ? (
              <img src={building.logo_data_url} alt="" className="h-5 w-5 object-contain" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-primary" />
            )}
            <span className="font-display font-semibold tracking-tight text-slate-900">{building.name || (daycare ? "Daycare Kiosk" : "School Kiosk")}</span>
          </div>
          <div className="flex items-center gap-3">
            {!daycare && ["admin", "officer"].includes(user?.role) && <button onClick={() => nav(user.role === "officer" ? "/bus-status" : "/admin/buses")} data-testid="kiosk-bus-status-btn" title="Bus status: delays & substitute buses"
              className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 border border-white/60 shadow-sm text-sm font-medium text-slate-700 hover:bg-white transition backdrop-blur-xl">
              <BusFront className="h-4 w-4 text-amber-600" /> Bus status
            </button>}
            <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 border border-emerald-200 shadow-sm" data-testid="on-duty-badge">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <UserCog className="h-4 w-4 text-emerald-700" />
              <span className="text-sm">
                <span className="text-emerald-700 font-medium">On duty:</span>{" "}
                <span className="font-semibold text-slate-900">{displayName(user?.name)}</span>
              </span>
            </div>
            {officerHasConsole(user) && (
              <button type="button" onClick={() => nav(officerAllowedModules(user)[0].path)} data-testid="kiosk-console-btn" title="Open my console"
                className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 border border-white/60 shadow-sm text-sm font-medium text-slate-700 hover:bg-white transition backdrop-blur-xl">
                <LayoutDashboard className="h-4 w-4 text-primary" /> Console
              </button>
            )}
            <button onClick={() => { setPwd(""); setSignOutOpen(true); }} data-testid="kiosk-officer-signout-btn" title="Officer sign out"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 border border-white/60 text-slate-500 hover:text-slate-900 hover:bg-white transition backdrop-blur-xl shadow-sm">
              <KeyRound className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="flex flex-1 min-h-0 items-center justify-center px-6 pb-6 [@media(max-height:820px)]:pb-3">
          <FitToScreen maxWidth={896} testid="school-kiosk-fit" className="rounded-3xl border border-white/60 bg-white/85 p-10 md:p-14 backdrop-blur-2xl shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)]">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-4">
              <Building2 className="h-4 w-4" />
              <span>{daycare ? "Front Desk · Sign-in / Sign-out" : "School Reception · Sign-in / Sign-out"}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
              {daycare ? `Welcome to ${building.name || "our center"}.` : "Hello, parent."}
            </h1>
            <p className="mt-4 text-xl text-slate-700 max-w-2xl">
              {daycare
                ? "Sign your child in or out with your family PIN. Front-desk staff can also check children in with an ID scan below."
                : "Choose what you're doing today. For pickup or early dismissal, you'll be asked to scan your driver's license, then type your child's full name."}
            </p>
            {daycare && <div className="mt-8"><ParentPinSignin /></div>}
            {daycare && <div className="mt-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Front-desk assisted (ID scan)</div>}

            <div className={`${daycare ? "mt-3" : "mt-10"} grid gap-4 justify-center ${
              shownActions.length === 1 ? "grid-cols-1 md:max-w-md md:mx-auto"
              : shownActions.length === 2 ? "grid-cols-1 md:grid-cols-2 md:max-w-2xl md:mx-auto"
              : shownActions.length === 3 ? "grid-cols-1 md:grid-cols-3 md:max-w-3xl md:mx-auto"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
            }`} data-testid="school-actions-grid">
              {shownActions.map(({ key, label, subtitle, Icon, accent }) => (
                <button
                  key={key}
                  onClick={() => startAction(key)}
                  data-testid={`school-action-${key}`}
                  disabled={loading}
                  className={`group flex flex-col items-start justify-between gap-5 rounded-2xl px-6 py-7 min-h-[170px] transition active:scale-[0.98] hover:shadow-lg disabled:opacity-50
                    ${accent === "primary" ? "bg-primary text-white"
                    : accent === "dark" ? "border-2 border-slate-900/90 bg-white text-slate-900 hover:bg-slate-900 hover:text-white"
                    : accent === "amber" ? "border-2 border-amber-400 bg-amber-50 text-amber-900"
                    : "border-2 border-orange-400 bg-orange-50 text-orange-900"}`}
                >
                  <Icon className="h-10 w-10" strokeWidth={2} />
                  <div>
                    <div className="font-display text-2xl font-semibold tracking-tight">{label}</div>
                    <div className="text-xs uppercase tracking-widest opacity-70 mt-0.5">{subtitle}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Non-parent visitor entry — vendors, district staff, contractors, parent-teacher meetings, etc. */}
            <div className="mt-8 pt-6 border-t border-slate-200/70">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">
                <Users className="h-3.5 w-3.5" /> Not here for a student? Visitors &amp; staff
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => nav("/kiosk/checkin")}
                  data-testid="school-visitor-checkin-btn"
                  className="group flex items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 text-left transition hover:border-primary/50 hover:shadow-md active:scale-[0.98]"
                >
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center transition group-hover:bg-primary group-hover:text-white">
                    <LogIn className="h-5 w-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display font-semibold text-slate-900">Visitor sign in</span>
                    <span className="block text-xs text-slate-500">Vendors, contractors &amp; guests</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-primary group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => nav("/kiosk/checkout")}
                  data-testid="school-visitor-checkout-btn"
                  className="group flex items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 text-left transition hover:border-slate-400 hover:shadow-md active:scale-[0.98]"
                >
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center transition group-hover:bg-slate-900 group-hover:text-white">
                    <LogOut className="h-5 w-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display font-semibold text-slate-900">Visitor sign out</span>
                    <span className="block text-xs text-slate-500">Leaving the building</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-slate-600 group-hover:translate-x-0.5" />
                </button>
                <StaffClockTile variant="chip" />
              </div>
            </div>
          </FitToScreen>
        </main>

        <footer className="px-12 py-6 [@media(max-height:820px)]:py-3 text-sm text-slate-700">
          <span className="rounded-full bg-white/70 px-4 py-1.5 backdrop-blur-xl border border-white/50">
            School Safety Log · Student roster is private
          </span>
        </footer>
      </div>

      <LicenseScanner open={scannerOpen} onClose={cancelFlow} onScanned={onScanned} />

      {/* Type-student-name dialog */}
      <Dialog open={nameDialogOpen} onOpenChange={(o) => { if (!o) cancelFlow(); }}>
        <DialogContent className="sm:max-w-md" data-testid="student-name-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              {actionLabel} · type the student's full name
            </DialogTitle>
            <DialogDescription>
              {scanData?.full_name ? (
                <span>Parent ID confirmed: <span className="font-semibold">{scanData.full_name}</span>{scanData.license_number ? ` · ${scanData.license_number}` : ""}. Now type your child's full name exactly as it appears on the school roster.</span>
              ) : (
                <span>Type your child's full name exactly as it appears on the school roster.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitTypedName} className="space-y-3 pt-2">
            {pickupError && (
              <div
                className="flex items-start gap-2.5 rounded-lg border-2 border-red-300 bg-red-50 px-3.5 py-3 text-sm text-red-800"
                data-testid="pickup-error-banner"
                role="alert"
              >
                <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
                <div>
                  <div className="font-semibold">Not authorized — student was NOT released.</div>
                  <div className="mt-0.5 text-red-700">{pickupError}</div>
                </div>
              </div>
            )}
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Student full name</span>
              <input
                type="text"
                autoFocus
                required
                autoComplete="off"
                value={typedName}
                onChange={(e) => { setTypedName(e.target.value); if (pickupError) setPickupError(""); }}
                placeholder="e.g. Jane Smith"
                data-testid="student-name-input"
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-3 text-base focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={cancelFlow} data-testid="student-name-cancel" className="h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={submitting || !typedName.trim()} data-testid="student-name-submit" className="h-11 rounded-lg bg-primary px-5 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sign-out dialog */}
      <Dialog open={signOutOpen} onOpenChange={setSignOutOpen}>
        <DialogContent className="sm:max-w-md" data-testid="kiosk-signout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> Officer sign out
            </DialogTitle>
            <DialogDescription>Enter your password to end your shift on this device.</DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmEndShift} className="space-y-3 pt-2">
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Password</span>
              <input type="password" autoFocus required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="kiosk-signout-password" className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            </label>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setSignOutOpen(false)} data-testid="kiosk-signout-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={verifying || !pwd} data-testid="kiosk-signout-confirm" className="h-10 rounded-lg bg-destructive px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {verifying && <Loader2 className="h-4 w-4 animate-spin" />} End shift
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
